import { useEffect, useRef, type ReactNode } from "react";
import { useExternalLinkHandler } from "@shared/lib/hooks/useExternalLinkHandler";
import { ExternalLinkModal } from "@shared/ui/external-link-modal/external-link-modal";

interface ExternalLinkHandlerProps {
  children: ReactNode;
  allowedDomains?: string[];
  showModal?: boolean;
}

export const ExternalLinkHandler = ({
  children,
  allowedDomains = [],
  showModal = true,
}: ExternalLinkHandlerProps) => {
  const {
    isModalOpen,
    pendingUrl,
    handleLinkClick,
    confirmNavigation,
    cancelNavigation,
  } = useExternalLinkHandler({ allowedDomains, showModal });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');
      
      if (!link) return;

      // Игнорируем якорные ссылки
      if (link.getAttribute('href') && link.getAttribute('href')?.startsWith('#')) {
        return;
      }

      // Игнорируем ссылки с rel="nofollow"
      const rel = link.getAttribute('rel');
      if (rel && rel.includes('nofollow')) {
        return;
      }

      // Перехватываем клик
      event.preventDefault();
      const href = link.getAttribute('href') || '';
      const targetAttr = link.getAttribute('target') || '';
      handleLinkClick(href, event as any, targetAttr);
    };

    container.addEventListener('click', handleClick);

    return () => {
      container.removeEventListener('click', handleClick);
    };
  }, [handleLinkClick]);

  return (
    <div ref={containerRef}>
      {children}
      
      {showModal && (
        <ExternalLinkModal
          isOpen={isModalOpen}
          url={pendingUrl}
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
        />
      )}
    </div>
  );
}; 